import React, { useState, useEffect } from 'react';
import { Plus, X, Check, Play, Eye, Loader } from 'lucide-react';
import styles from './AddingHomepageHeroSection.module.css';
import { useBlogs } from '../../../../../hooks/useBlogs';
import { 
  useHeroSectionsByType, 
  useCreateHeroSection, 
  useUpdateHeroSection, 
  useDeleteHeroSection 
} from '../../../../../hooks/herosection/useherosectionHooks';
import { toast } from 'react-toastify';

function AddingBlogpage() {
  const { blogs, isLoading: isBlogsLoading, error } = useBlogs();
  
  const { 
    heroSections, 
    isLoading: isLoadingHeroSections, 
    refetchHeroSections 
  } = useHeroSectionsByType('Blog');
  
  const createHeroSectionMutation = useCreateHeroSection();
  const updateHeroSectionMutation = useUpdateHeroSection();
  const deleteHeroSectionMutation = useDeleteHeroSection();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [seriesNumber, setSeriesNumber] = useState(1);
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [heroSeries, setHeroSeries] = useState([]);
  const [animateHero, setAnimateHero] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use blogs from API if available and valid, otherwise use sample data
  const blogsList = Array.isArray(blogs?.blogs) ? blogs.blogs : [];

  

  // Load hero sections from the API
  useEffect(() => {
    if (heroSections && heroSections.length > 0) {
      const heroWithDetails = heroSections.map(hero => {
        // Use optional chaining and provide fallbacks
        return {
          ...hero,
          title: hero.blog_data?.title || 'Untitled Blog',
          category: hero.blog_data?.category || { name: 'Uncategorized' },
          creator: hero.blog_data?.creator || { name: 'Suno Kahnaiyan' },
          coverImageUrl: hero.blog_data?.featured_image || '/default-image.jpg',
          description: hero.blog_data?.description || '',
          position: hero.series_no
        };
      });
      setHeroSeries(heroWithDetails);
    }
  }, [heroSections]);

  useEffect(() => {
    // Trigger animation when component mounts
    setAnimateHero(true);
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSeriesId('');
    setSelectedSeries(null);
  };

  const handleSeriesSelect = (e) => {
    const id = e.target.value;
    setSelectedSeriesId(id);
    
    // Find the selected series from the list
    const found = blogsList.find(item => item.id === id);
    setSelectedSeries(found);
  };

  const handleSave = async () => {
    if (!selectedSeries || !selectedSeriesId) return;
    
    setIsSubmitting(true);
    try {
      // Check if there's an existing hero section at this position
      const existingHero = heroSections.find(hero => hero.series_no === seriesNumber);
      
      if (existingHero) {
        // Update existing hero section
        await updateHeroSectionMutation.mutateAsync({
          id: existingHero.id,
          series_no: seriesNumber,
          blog_id: selectedSeriesId,
          type: 'Blog',
          is_active: true
        });
      } else {
        // Create new hero section
        await createHeroSectionMutation.mutateAsync({
          series_no: seriesNumber,
          blog_id: selectedSeriesId,
          type: 'Blog',
          is_active: true
        });
      }
      
      // Refresh hero sections
      await refetchHeroSections();
      
      setIsModalOpen(false);
      setSelectedSeriesId('');
      setSelectedSeries(null);
    } catch (error) {
      console.error('Error saving hero section:', error);
      toast.error('Failed to save hero section');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeHeroItem = async (id) => {
    try {
      await deleteHeroSectionMutation.mutateAsync(id);
      // Refresh hero sections after deletion
      await refetchHeroSections();
    } catch (error) {
      console.error('Error removing hero section:', error);
      toast.error('Failed to remove hero section');
    }
  };

  if (isLoadingHeroSections || isBlogsLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader className={styles.loadingSpinner} />
        <p>Loading hero sections...</p>
      </div>
    );
  }

  if (error) {
    console.error("Error loading blogs:", error);
    // Continue with available data
  }

  return (
    <div className={styles.container}>
      <div className={`${styles.header} ${animateHero ? styles.fadeIn : ''}`}>
        <h1>Blogpage Hero Section</h1>
        <button 
          className={styles.addButton}
          onClick={handleOpenModal}
          disabled={heroSeries.length >= 4}
        >
          <Plus size={20} />
          Add Hero
        </button>
      </div>

      {/* Display existing hero settings */}
      <div className={styles.currentHero}>
        {heroSeries.length > 0 ? (
          <div className={`${styles.heroGrid} ${animateHero ? styles.slideInUp : ''}`}>
{heroSeries.map((item) => (
  <div key={item.id} className={styles.heroCard}>
    <div className={styles.heroImageContainer}>
      <img src={item.coverImageUrl} alt={item.title} />
      <div className={styles.heroOverlay}>
        <div className={styles.heroActions}>
          <button className={styles.playButton}>
            <Play size={24} />
          </button>
          <button 
            className={styles.removeButton} 
            onClick={() => removeHeroItem(item.id)}
          >
            <X size={18} />
          </button>
        </div>
        <div className={styles.positionBadge}>Position {item.position}</div>
      </div>
    </div>
    <div className={styles.heroContent}>
      <h3>{item.title}</h3>
      <div className={styles.heroMeta}>
        <span className={styles.category}>
          {item.category?.name || 'Uncategorized'}
        </span>
        <span className={styles.creator}>
          By {item.creator?.name || 'Unknown Author'}
        </span>
      </div>
      <p className={styles.description}>{item.description}</p>
    </div>
  </div>
))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <h2>No Hero Sections Added Yet</h2>
            <p>Click the "Add Hero" button to showcase featured content on your homepage.</p>
            <button className={styles.addEmptyButton} onClick={handleOpenModal}>
              <Plus size={20} />
              Add Your First Hero Section
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${styles.fadeInScale}`}>
            <div className={styles.modalHeader}>
              <h2>Add Hero Section</h2>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="seriesNumber">Hero Position (1-4):</label> 
                <div className={styles.positionSelector}>
                  {[1, 2, 3, 4].map(num => (
                    <button
                      key={num}
                      className={`${styles.positionButton} ${seriesNumber === num ? styles.activePosition : ''}`}
                      onClick={() => setSeriesNumber(num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="seriesSelect">Select Blog:</label>
                <select
                  id="seriesSelect"
                  value={selectedSeriesId}
                  onChange={handleSeriesSelect}
                  className={styles.enhancedSelect}
                >
                  <option value="">-- Select a Blog --</option>
                  {blogsList.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedSeriesId && (
                <div className={`${styles.previewSection} ${styles.slideIn}`}>
                  <h3>Preview</h3>
                  <div className={styles.previewCard}>
                    <div className={styles.previewImage}>
                      {selectedSeries && (
                        <img
                          src={selectedSeries.featuredImage}                         
                          alt={selectedSeries.title}
                        />
                      )}
                    </div>
                    {selectedSeries && (
                      <div className={styles.previewInfo}>
                        <h4>{selectedSeries.title}</h4>
                        <div className={styles.previewMeta}>
                          <span className={styles.category}>{selectedSeries.category.name}</span>
                          <span className={styles.previewStats}>
                            <Eye size={14} />
                            <span>{selectedSeries.viewsCount?.toLocaleString() || 0}</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.cancelButton} 
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSave}
                disabled={!selectedSeriesId || isSubmitting}
              >
                {isSubmitting ? (
                  <Loader size={16} className={styles.spinner} />
                ) : (
                  <Check size={16} />
                )}
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddingBlogpage;